/**
 * Effect state bitfield constants.
 *
 * A single `flags: number` field on EffectRecord replaces 4 individual booleans.
 * Memory: 1 field vs 4 fields per effect → ~48 bytes saved per 1000 effects.
 * Comparison: `(flags & EFFECT_ACTIVE) !== 0` is one bitwise op vs a boolean load.
 */

/** Effect is live and allowed to run. Cleared by `.stop()`. */
export const EFFECT_ACTIVE = 0b0001  // 1

/** Effect has a stale dependency and needs to re-run. Set by trigger(), cleared after run. */
export const EFFECT_DIRTY = 0b0010  // 2

/** Effect is already queued in the scheduler. Guards against double-queueing. */
export const EFFECT_SCHEDULED = 0b0100  // 4

/** Effect is paused (e.g., component unmounting). Won't run even if dirty. */
export const EFFECT_PAUSED = 0b1000  // 8

// --- Helpers ----------------------------------------------------------------

export function isActive(flags: number): boolean { return (flags & EFFECT_ACTIVE) !== 0 }
export function isDirty(flags: number): boolean { return (flags & EFFECT_DIRTY) !== 0 }
export function isScheduled(flags: number): boolean { return (flags & EFFECT_SCHEDULED) !== 0 }
export function isPaused(flags: number): boolean { return (flags & EFFECT_PAUSED) !== 0 }

export function setActive(flags: number): number { return flags | EFFECT_ACTIVE }
export function setDirty(flags: number): number { return flags | EFFECT_DIRTY }
export function setScheduled(flags: number): number { return flags | EFFECT_SCHEDULED }
export function setPaused(flags: number): number { return flags | EFFECT_PAUSED }

export function clearActive(flags: number): number { return flags & ~EFFECT_ACTIVE }
export function clearDirty(flags: number): number { return flags & ~EFFECT_DIRTY }
export function clearScheduled(flags: number): number { return flags & ~EFFECT_SCHEDULED }
export function clearPaused(flags: number): number { return flags & ~EFFECT_PAUSED }
